// Columna central del explorer (`.md-list` del mock): seis píldoras, el conteo, la tabla de la
// cartera y el pie con «Cargar más».
import type { Table } from '@tanstack/react-table';
import { RefreshCw } from 'lucide-react';
import type { PortalCustomerRow } from '@/api/services/retail-wizard/contract';
import { ErrorState } from '@/kit';
import { cn } from '@/lib/cn';
import { ACCOUNT_ONLY_LABEL } from '@/shared/lib/format/accountOnly';
import { formatMXN } from '@/shared/lib/format/money';
import { DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import {
    CUSTOMERS_NOUN,
    LoadedScopeFooter,
    loadedResultLabel,
    type LoadedScope,
} from '@/shared/patterns/LoadedScope';
import { tierLabel } from '@/shared/patterns/Pills/tierPresentation';
import { StatsBar, type StatsBarItem } from '@/shared/patterns/StatsBar';
import { formatPhone, PENDING_CELL } from '../lib/customersFormat';
import {
    accountAgeLabel,
    daysLabel,
    daysToneClass,
    levelOutlineClass,
    STATUS_CHIP_CLASS,
    STATUS_CHIP_TITLE,
    STATUS_LABEL,
    type PortalChipStatus,
    tierOf,
} from '../lib/portalCustomerFormat';
import type { CustomersListErrorCopy, PillCount } from '../lib/portalCustomersCopy';
import {
    nextSort,
    type CustomersSegment,
    type SortKey,
    type SortState,
} from '../lib/portalCustomersFilters';

/** Las columnas del mock sin el folio `CL-XXXX`, que no existe: el cliente se queda con el resto. */
const COLUMNS: DataTableColumn<PortalCustomerRow, SortKey>[] = [
    {
        key: 'customer',
        label: 'Cliente',
        sortKey: 'customerName',
        cell: (row) => (
            <>
                <div className="truncate font-semibold">{row.customerName ?? PENDING_CELL}</div>
                <div className="mt-px truncate text-[9.5px] tabular-nums text-muted-foreground">
                    {formatPhone(row.customerPhone)}
                </div>
            </>
        ),
    },
    {
        key: 'status',
        label: 'Estado',
        width: '88px',
        sortKey: 'status',
        cellOverflow: 'nowrap',
        cell: (row) =>
            row.accountOnly || row.status === null ? (
                <StatusChips status="ACCOUNT_ONLY" />
            ) : (
                <StatusChips status={row.status} repurchase={row.repurchaseCandidate} />
            ),
    },
    {
        key: 'level',
        label: 'Nivel SK',
        width: '78px',
        sortKey: 'level',
        cellOverflow: 'nowrap',
        cell: (row) => <LevelPill clientLevel={row.clientLevel} />,
    },
    {
        key: 'purchasesCount',
        label: 'Compras',
        width: '84px',
        numeric: true,
        sortKey: 'purchasesCount',
        cell: (row) => row.purchasesCount.toLocaleString('es-MX'),
    },
    {
        key: 'totalSoldCents',
        label: 'Total $',
        width: '80px',
        numeric: true,
        sortKey: 'totalSoldCents',
        cell: (row) => (row.accountOnly ? '—' : formatMXN(row.totalSoldCents / 100)),
    },
    {
        key: 'avgTicketCents',
        label: 'Ticket',
        width: '74px',
        numeric: true,
        sortKey: 'avgTicketCents',
        cell: (row) => (row.accountOnly ? '—' : formatMXN(row.avgTicketCents / 100)),
    },
    {
        key: 'daysSinceLastPurchase',
        label: 'Días',
        width: '58px',
        numeric: true,
        sortKey: 'daysSinceLastPurchase',
        note: 'Días sin comprarte: desde su última compra contigo; si solo tiene cuenta, desde su alta',
        cellClassName: (row) =>
            row.daysSinceLastPurchase === null
                ? 'text-muted-foreground'
                : daysToneClass(row.daysSinceLastPurchase),
        cell: (row) =>
            row.accountOnly || row.daysSinceLastPurchase === null
                ? accountAgeLabel(row.createdAt)
                : daysLabel(row.daysSinceLastPurchase),
    },
    {
        key: 'favoriteStoreName',
        label: 'Tienda fav',
        width: '90px',
        sortKey: 'favoriteStoreName',
        note: 'Tienda donde más te ha comprado',
        cellTitle: (row) => row.favoriteStoreName ?? undefined,
        cell: (row) => row.favoriteStoreName ?? PENDING_CELL,
    },
];

/** Las píldoras del mock con rótulos que caben; el `title` dice qué cuentan. */
const SEGMENT_PILLS: { key: CustomersSegment; label: string; tone?: StatsBarItem['tone'] }[] = [
    { key: 'all', label: 'Todos' },
    { key: 'active', label: 'Activos', tone: 'success' },
    { key: 'dormant', label: 'Dormidos', tone: 'muted' },
    { key: 'repurchase', label: 'Recompra', tone: 'warning' },
    { key: 'recurrent', label: 'Recurrentes' },
    { key: 'skCredit', label: 'Crédito SK', tone: 'primary' },
    { key: 'accountOnly', label: ACCOUNT_ONLY_LABEL, tone: 'info' },
];

export function CustomersTable({
    table,
    pageRows,
    shownCount,
    scope,
    segment,
    pills,
    onSegmentChange,
    loadedNote,
    emptyNote,
    creditUnavailableCount,
    sort,
    onSortChange,
    selectedId,
    onSelect,
    isLoading,
    errorCopy,
    onRetry,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    nextPageError,
}: {
    table: Table<PortalCustomerRow>;
    /** Las filas de la página que se ve, ya filtradas y ordenadas. */
    pageRows: PortalCustomerRow[];
    /** Cuántas filas cargadas pasan los filtros. */
    shownCount: number;
    scope: LoadedScope;
    segment: CustomersSegment;
    pills: Record<CustomersSegment, PillCount>;
    onSegmentChange: (next: CustomersSegment) => void;
    /** Qué mide solo lo cargado mientras queden páginas. */
    loadedNote: string | null;
    emptyNote: string;
    /** Clientes cargados cuyo nivel SK no se pudo leer. */
    creditUnavailableCount: number;
    sort: SortState;
    onSortChange: (next: SortState) => void;
    selectedId: string | null;
    onSelect: (retailCustomerId: string) => void;
    isLoading: boolean;
    /** La primera página no llegó: el motivo en lugar de la tabla. */
    errorCopy: CustomersListErrorCopy | null;
    onRetry: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    /** Por qué falló la última página pedida; lo cargado sigue en la tabla. */
    nextPageError: string | null;
}) {
    // Una lectura fallida NO se pinta como cartera vacía: el vendedor concluiría que no tiene clientes.
    if (errorCopy) {
        return (
            <ErrorState
                className="m-3"
                title={errorCopy.title}
                description={errorCopy.description}
                onRetry={errorCopy.canRetry ? onRetry : undefined}
            />
        );
    }

    return (
        <>
            <StatsBar
                items={SEGMENT_PILLS.map((pill) => ({
                    ...pill,
                    value: isLoading ? PENDING_CELL : pills[pill.key].value,
                    title: isLoading ? undefined : pills[pill.key].title,
                }))}
                active={segment}
                onChange={onSegmentChange}
                ariaLabel="Recortes de tu cartera"
                className="h-10 py-0"
            />

            {loadedNote === null || isLoading ? null : (
                <p
                    role="status"
                    className="flex-none border-b border-border bg-muted/40 px-3 py-1.5 text-[10px] leading-snug text-muted-foreground"
                >
                    {loadedNote}
                </p>
            )}

            <div className="flex flex-none items-center border-b border-border/50 px-3 py-1">
                <span className="text-[10.5px] font-semibold text-muted-foreground">
                    {isLoading
                        ? `${PENDING_CELL} clientes`
                        : loadedResultLabel(shownCount, scope, CUSTOMERS_NOUN)}
                    {isFetchingNextPage ? ' · cargando más…' : ''}
                </span>
            </div>

            <DataTable
                columns={COLUMNS}
                rows={pageRows}
                rowKey={(row) => row.retailCustomerId}
                caption="Tu cartera de clientes"
                sort={sort}
                onSortToggle={(key) => {
                    onSortChange(nextSort(sort, key));
                }}
                onRowClick={(row) => {
                    onSelect(row.retailCustomerId);
                }}
                isRowSelected={(row) => row.retailCustomerId === selectedId}
                isLoading={isLoading}
                empty={<span className="text-[11.5px] italic leading-normal">{emptyNote}</span>}
            />

            {creditUnavailableCount > 0 ? (
                <p className="flex-none border-t border-border px-3 py-1.5 text-[10px] leading-snug text-muted-foreground">
                    No se pudo leer el nivel SK de {creditUnavailableCount}{' '}
                    {creditUnavailableCount === 1 ? 'cliente' : 'clientes'}; aparecen «Sin nivel»
                    hasta que vuelvas a cargar.
                </p>
            ) : null}

            <LoadedScopeFooter
                noun={CUSTOMERS_NOUN}
                scope={isLoading ? null : scope}
                shown={isLoading ? null : shownCount}
                table={table}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={onLoadMore}
                nextPageError={nextPageError}
            />
        </>
    );
}

/** Chip cuadrado de estado y, si aplica, el de candidato a recompra. */
export function StatusChips({
    status,
    repurchase = false,
}: {
    status: PortalChipStatus;
    repurchase?: boolean;
}) {
    return (
        <span className="inline-flex items-center gap-1">
            <span
                title={STATUS_CHIP_TITLE[status]}
                className={cn(
                    'inline-flex items-center rounded-[3px] px-1.5 py-px text-[9.5px] font-bold uppercase tracking-[0.03em]',
                    STATUS_CHIP_CLASS[status],
                )}
            >
                {STATUS_LABEL[status]}
            </span>
            {repurchase ? (
                <span
                    title="Candidato a recompra"
                    className="inline-flex items-center rounded-[3px] bg-warning-soft px-1 py-[2px] text-warning"
                >
                    <RefreshCw className="size-2.5" aria-label="Candidato a recompra" />
                </span>
            ) : null}
        </span>
    );
}

/** `.nivel-pill` del mock: rótulo completo en contorno del color del metal. */
export function LevelPill({
    clientLevel,
    prefix,
    unread = false,
}: {
    clientLevel: string | null;
    /** «Nivel SK · » en la ficha. */
    prefix?: string;
    /** El crédito no se pudo leer: se dice, no se pinta «Sin nivel». */
    unread?: boolean;
}) {
    const level = tierOf(clientLevel);
    return (
        <span
            title={unread ? 'No se pudo leer el crédito del cliente' : undefined}
            className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-full border bg-card px-1.5 py-0.5 text-[9.5px] font-semibold',
                unread ? 'border-border text-muted-foreground' : levelOutlineClass(level),
            )}
        >
            {prefix}
            {unread ? 'sin leer' : tierLabel(level, 'short')}
        </span>
    );
}
